'use client';
import React, { Fragment, useRef } from 'react';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { noParagraphs, paragraphs } from './PortableTextComponents';
import { StandardCard, SlimCard } from '@/components/shared/cards';
import { m, useInView } from 'framer-motion';
import Pattern from '@/public/images/Pattern.png';
import Image from 'next/image';
import { fadeUp, fadeUpRight } from '@/assets/framer/animations';
import { MenuItemLink } from '@/types/types';
import { CardCarousel } from '@/components/shared/slider';
import { backgroundClasses, sectionPadding } from '../utils/utils';
import { DisplayOptionsType } from '@/types/types';

export type CardType = {
  cardType: 'standard' | 'slim';
  title?: PortableTextBlock;
  description?: PortableTextBlock;
  image?: {
    url?: string;
    alt?: string;
    mediaAlt?: string;
    placeholderUrl?: string;
  };
  button: {
    text: string;
    link?: MenuItemLink;
  };
};

export default function CardsSection(props: {
  data?: {
    sectionId?: string;
    heading?: PortableTextBlock;
    textContent: PortableTextBlock;
    displayOptions: {
      cardType: 'standard' | 'slim';
      layout: 'slider' | 'grid';
      padding: 'py' | 'pt' | 'pb' | 'p-0';
      showBackgroundPattern: boolean;
      showBackgroundStrip: boolean;
      backgroundStripSize: 'thin' | 'tall';
      background: DisplayOptionsType['background'];
      customBackground: DisplayOptionsType['customBackground'];
      centerText: boolean;
      innerPadding: 'default' | 'tight';
    };
    cards: CardType[];
  };
}) {
  const sectionId = props.data?.sectionId;
  const heading = props.data?.heading;
  const textContent = props.data?.textContent;
  const centerText = props.data?.displayOptions?.centerText;
  const displayOptions = props.data?.displayOptions;
  const cards = props.data?.cards;
  const sectionPaddingClass = sectionPadding(displayOptions?.padding);
  const backgroundClass = backgroundClasses(displayOptions);

  const headingRef = useRef(null);
  const section = useRef(null);
  const isInView = useInView(section, {
    amount: 0.3,
    once: true,
  });

  return (
    <section
      id={sectionId}
      ref={section}
      className={`${sectionPaddingClass} ${backgroundClass} relative`}
    >
      <div className='overflow-hidden'>
        <div
          className={`container-padding container relative mx-auto scroll-m-16 ${displayOptions?.innerPadding === 'tight' ? '' : 'pb-4'}`}
        >
          <div
            className={
              centerText ? 'flex flex-col items-center text-center' : ''
            }
          >
            <m.h2
              initial='hidden'
              animate={isInView ? 'visible' : 'hidden'}
              variants={fadeUp}
              ref={headingRef}
              className='heading-xl relative z-10 max-w-sm font-display font-medium tracking-tighter text-charcoal sm:max-w-5xl [&_strong]:text-pink'
            >
              {heading && (
                <PortableText
                  value={heading}
                  components={noParagraphs}
                ></PortableText>
              )}
            </m.h2>

            {textContent && (
              <m.div
                className={`max-w-sm space-y-5 ${displayOptions?.innerPadding === 'tight' ? 'pt-5' : 'pt-10'} text-base text-charcoal sm:max-w-5xl sm:text-lg lg:text-2xl`}
                initial='hidden'
                animate={isInView ? 'visible' : 'hidden'}
                variants={fadeUp}
                transition={{ delay: 0.2 }}
                ref={headingRef}
              >
                <PortableText
                  value={textContent}
                  components={paragraphs}
                ></PortableText>
              </m.div>
            )}
          </div>
        </div>

        <div
          className={`relative pb-4 ${displayOptions?.innerPadding === 'tight' ? '' : 'pt-4'} ${
            (displayOptions?.showBackgroundStrip &&
              displayOptions?.backgroundStripSize === 'tall') ||
            true
              ? ''
              : `mt-6 ${displayOptions?.cardType === 'slim' ? '' : 'lg:mt-14 lg:gap-8'}`
          }`}
        >
          <div
            className={`container-padding container relative mx-auto ${displayOptions?.backgroundStripSize === 'tall' ? 'py-3' : ''}`}
          >
            {displayOptions?.layout === 'slider' ? (
              <>
                <CardCarousel
                  cardType={displayOptions?.cardType}
                  cards={cards}
                />
              </>
            ) : (
              <>
                <m.div
                  initial='hidden'
                  animate={isInView ? 'visible' : 'hidden'}
                  variants={fadeUp}
                  transition={{ delay: 0.2, staggerChildren: 0.1 }}
                  className='relative z-10 grid items-stretch gap-8 md:grid-cols-2 md:gap-6 xl:grid-cols-3 xl:gap-10 2xl:gap-12'
                >
                  {cards &&
                    cards.length > 0 &&
                    cards.map((card, index) => (
                      <Fragment key={index}>
                        {displayOptions?.cardType === 'slim' ? (
                          <SlimCard card={card} />
                        ) : (
                          <StandardCard card={card} />
                        )}
                      </Fragment>
                    ))}
                </m.div>
              </>
            )}
          </div>

          {displayOptions?.showBackgroundStrip &&
            displayOptions?.backgroundStripSize === 'tall' && (
              <div
                className={`absolute left-0 top-0 h-full w-full bg-[#FEF3D7]`}
              >
                <div
                  className='relative h-full w-full bg-center opacity-50 [background-repeat:_repeat_-1px]'
                  style={{
                    backgroundImage: `url(${Pattern.src}`,
                  }}
                ></div>
              </div>
            )}
        </div>
      </div>

      {/* Background SVG */}
      {displayOptions?.showBackgroundPattern && (
        <m.svg
          className='absolute right-0 top-52 z-0 hidden lg:block [&_path]:fill-pink'
          initial='hidden'
          animate={isInView ? 'visible' : 'hidden'}
          variants={fadeUpRight}
          transition={{ delay: 0.3 }}
          xmlns='http://www.w3.org/2000/svg'
          width='234'
          height='203'
          viewBox='0 0 234 203'
          fill='none'
        >
          <path
            d='M64.0621 133.367L52.077 133.344L52.0992 121.952L64.0843 121.976L64.0621 133.367ZM77.137 121.095L65.5943 121.073L65.6177 109.057L77.1604 109.079L77.137 121.095ZM90.5362 107.495L78.4505 107.472L78.4743 95.2748L90.56 95.2984L90.5362 107.495ZM103.995 94.4993L91.6475 94.4753L91.6733 81.2518L104.02 81.2759L103.995 94.4993ZM116.992 80.4558L104.907 80.4323L104.932 67.5711L117.017 67.5947L116.992 80.4558ZM130.68 94.2696L118.594 94.246L118.62 81.3043L130.705 81.3279L130.68 94.2696ZM144.188 107.318L132.102 107.295L132.126 95.0976L144.212 95.1211L144.188 107.318ZM157.474 120.688L145.389 120.665L145.412 108.911L157.497 108.934L157.474 120.688ZM145.722 135.298L157.808 135.321L157.786 146.713L145.7 146.69L145.722 135.298ZM132.385 148.274L144.47 148.297L144.447 160.494L132.361 160.471L132.385 148.274ZM105.084 175.05L117.17 175.074L117.145 187.935L105.059 187.911L105.084 175.05ZM91.8799 160.955L104.227 160.979L104.201 174.203L91.8541 174.179L91.8799 160.955ZM78.7337 147.908L90.8193 147.931L90.7956 160.128L78.7099 160.105L78.7337 147.908ZM90.144 133.297L78.6818 133.275L78.7036 122.085L90.1658 122.107L90.144 133.297ZM103.823 120.845L91.7369 120.822L91.7598 109.068L103.845 109.091L103.823 120.845ZM130.789 120.898L118.442 120.874L118.465 108.858L130.812 108.882L130.789 120.898ZM118.754 146.637L118.775 135.507L130.901 135.53C130.901 135.53 131.042 135.47 131.123 135.43L131.101 146.661L118.754 146.637ZM116.829 133.349L104.743 133.326L104.765 122.135L116.851 122.159L116.829 133.349ZM92.0704 135.455L104.156 135.478L104.135 146.347L92.0492 146.323L92.0704 135.455ZM105.419 147.96L117.766 147.984L117.741 160.442L105.394 160.418L105.419 147.96ZM143.795 133.664L131.669 133.64C131.669 133.64 131.528 133.7 131.448 133.74L131.471 121.926L143.818 121.95L143.795 133.684L143.795 133.664ZM117.483 107.528L105.136 107.504L105.16 95.045L117.507 95.0691L117.483 107.528ZM65.9077 135.686L77.4504 135.708L77.4297 146.315L65.887 146.293L65.9077 135.686ZM130.886 174.255L118.8 174.231L118.826 161.29L130.911 161.313L130.886 174.255ZM170.48 133.736L158.394 133.712L158.417 121.978L170.503 122.002L170.48 133.736ZM37.984 121.18C37.6824 121.179 37.4406 121.42 37.44 121.722L37.4136 135.268C37.413 135.57 37.6539 135.812 37.9555 135.812L51.2678 135.838L51.2453 147.411C51.2447 147.713 51.4855 147.955 51.7871 147.956L64.0739 147.98L64.0498 160.358C64.0492 160.66 64.29 160.902 64.5916 160.902L77.2202 160.927L77.1944 174.17C77.1938 174.472 77.4347 174.714 77.7363 174.715L90.4253 174.74L90.399 188.245C90.3984 188.547 90.6392 188.789 90.9408 188.789L103.992 188.815L103.966 201.978C103.965 202.28 104.206 202.522 104.508 202.522L118.041 202.549C118.343 202.549 118.585 202.308 118.586 202.006L118.611 188.843L131.179 188.868C131.481 188.868 131.723 188.627 131.723 188.325L131.749 175.082L144.921 175.107C145.222 175.108 145.464 174.867 145.465 174.565L145.491 161.322L158.481 161.347C158.783 161.347 159.025 161.106 159.025 160.804L159.049 148.326L171.819 148.351C172.12 148.351 172.362 148.11 172.363 147.808L172.387 135.35L184.513 135.373C184.815 135.374 185.056 135.133 185.057 134.831L185.083 121.285C185.084 120.984 184.843 120.742 184.542 120.741L172.054 120.717L172.079 107.916C172.079 107.614 171.838 107.372 171.537 107.371L158.767 107.346L158.792 94.8678C158.792 94.5659 158.551 94.3239 158.25 94.3233L145.259 94.298L145.285 81.0544C145.286 80.7525 145.045 80.5105 144.743 80.5099L131.572 80.4842L131.597 67.2608C131.598 66.9589 131.357 66.7169 131.055 66.7163L118.487 66.6918L118.513 53.5288C118.513 53.2269 118.273 52.9849 117.971 52.9843L104.437 52.9579C104.136 52.9573 103.894 53.1984 103.893 53.5003L103.868 66.6634L90.8169 66.6379C90.5153 66.6373 90.2735 66.8784 90.2729 67.1803L90.2466 80.6855L77.5576 80.6608C77.256 80.6602 77.0142 80.9012 77.0136 81.2031L76.9878 94.4467L64.3592 94.4221C64.0576 94.4215 63.8158 94.6626 63.8152 94.9645L63.7911 107.343L51.5044 107.319C51.2027 107.318 50.961 107.559 50.9604 107.861L50.9344 121.205L37.984 121.18Z'
            fill='none'
          />
          <path
            d='M191.984 28.1507C192.285 28.1513 192.527 27.9103 192.528 27.6084L192.553 14.8277L205.764 14.8535L205.739 27.6341C205.739 27.936 205.98 28.178 206.281 28.1786L218.588 28.2026L218.563 41.3656L205.793 41.3407C205.492 41.3402 205.25 41.5812 205.249 41.8831L205.224 54.9858L192.796 54.9616L192.822 41.9193C192.822 41.6174 192.581 41.3754 192.28 41.3748L179.168 41.3492L179.194 28.1258L191.964 28.1507L191.984 28.1507ZM164.99 42.1066L178.101 42.1321L178.075 55.5769C178.074 55.8788 178.315 56.1208 178.617 56.1214L191.949 56.1474L191.924 69.1897C191.923 69.4916 192.164 69.7336 192.466 69.7342L206.14 69.7608C206.442 69.7614 206.683 69.5204 206.684 69.2185L206.71 56.0957L219.479 56.1205C219.781 56.1211 220.022 55.8801 220.023 55.5782L220.049 42.1333L232.818 42.1582C233.12 42.1588 233.362 41.9178 233.362 41.6159L233.389 27.9496C233.39 27.6477 233.149 27.4057 232.847 27.4051L220.54 27.3812L220.566 13.9967C220.567 13.6948 220.326 13.4528 220.025 13.4522L206.592 13.4261L206.617 0.625289C206.617 0.323384 206.376 0.0813902 206.075 0.0808024L192.42 0.0541948C192.119 0.053607 191.877 0.294661 191.876 0.596566L191.852 13.3571L178.419 13.3309C178.117 13.3303 177.875 13.5714 177.875 13.8733L177.848 27.3181L165.099 27.2933C164.798 27.2927 164.556 27.5337 164.555 27.8356L164.529 41.5019C164.528 41.8038 164.769 42.0457 165.07 42.0463'
            fill='none'
          />
          <path
            d='M28.4199 44.6382L15.6103 44.6132L15.6337 32.5974L28.4433 32.6223L28.4199 44.6382ZM1.38942 46.4573L14.7017 46.4832L14.6776 58.8412C14.6771 59.1431 14.9179 59.3851 15.2196 59.3857L28.8938 59.4123C29.1955 59.4129 29.4373 59.1719 29.4379 58.87L29.4639 45.5258L42.6354 45.5514C42.937 45.552 43.1788 45.311 43.1794 45.0091L43.2061 31.3227C43.2066 31.0208 42.9658 30.7788 42.6642 30.7782L29.3519 30.7523L29.376 18.3943C29.3766 18.0924 29.1357 17.8504 28.834 17.8498L15.1598 17.8232C14.8581 17.8226 14.6164 18.0637 14.6158 18.3656L14.5898 31.7098L1.41821 31.6841C1.11657 31.6835 0.874811 31.9246 0.874222 32.2265L0.847591 45.8927C0.847003 46.1946 1.08782 46.4366 1.38946 46.4372'
            fill='none'
          />
        </m.svg>
      )}

      {displayOptions?.showBackgroundStrip &&
        displayOptions?.backgroundStripSize !== 'tall' && (
          <div
            className={` ${
              displayOptions?.padding === 'py'
                ? 'top-32 lg:top-[50%]'
                : displayOptions?.padding === 'pt'
                  ? 'top-32 lg:top-[50%]'
                  : 'top-32 lg:top-[40%]'
            } absolute left-0 h-28 w-full translate-y-full transform overflow-hidden bg-yellow-100`}
          >
            <Image
              className='h-full w-full min-w-[1920px] object-cover opacity-70'
              src={Pattern}
              alt={'pattern'}
              width={1920}
              height={100}
              loading='lazy'
            />
          </div>
        )}
    </section>
  );
}
